// ============================================================================
// derivePersonQualities — qualites CUMULEES (MASTER-GEST-GOUVERNANCE-V2 2026-07-26)
// ============================================================================
// Ecran Acces & gouvernance uniquement. derivePersonRole reste la source pour
// organigramme, rosters et cartes d'equipe (role unique par precedence).
//
// Ordre d'affichage stable (documente) :
//   1. admin       — systemCRole === 'ADMIN'
//   2. director    — isTeamDirector (gest.Teams.director_email)
//   3. manager     — systemCRole === 'GESTIONNAIRE'
//   4. employee    — systemCRole CONNU sans qualite elevee ci-dessus
//
// Doctrine anti-faux CONSERVEE :
//   - systemCRole absent (undefined) → jamais employee ; director seul si applicable
//   - systemCRole null / UTILISATEUR / autre connu → employee si pas admin/manager/director

import type { DerivedPersonRole, DerivePersonRoleInput } from './derivePersonRole';
import { personRoleI18nKey } from './derivePersonRole';

export type { DerivedPersonRole, DerivePersonRoleInput };

/** Ordre stable d'affichage des qualites cumulees (gouvernance). */
export const PERSON_QUALITY_DISPLAY_ORDER: readonly DerivedPersonRole[] = [
  'admin',
  'director',
  'manager',
  'employee',
] as const;

const QUALITY_RANK = new Map<DerivedPersonRole, number>(
  PERSON_QUALITY_DISPLAY_ORDER.map((role, index) => [role, index]),
);

function sortQualities(qualities: DerivedPersonRole[]): DerivedPersonRole[] {
  return [...qualities].sort(
    (a, b) => (QUALITY_RANK.get(a) ?? 99) - (QUALITY_RANK.get(b) ?? 99),
  );
}

/**
 * Retourne toutes les qualites cumulees d'une personne (non exclusives).
 * Tableau vide = aucune qualite affirmable (information non portee).
 */
export function derivePersonQualities(input: DerivePersonRoleInput): DerivedPersonRole[] {
  const qualities: DerivedPersonRole[] = [];

  if (input.systemCRole === 'ADMIN') {
    qualities.push('admin');
  }
  if (input.isTeamDirector) {
    qualities.push('director');
  }
  if (input.systemCRole === 'GESTIONNAIRE') {
    qualities.push('manager');
  }

  const hasElevated =
    qualities.includes('admin') ||
    qualities.includes('director') ||
    qualities.includes('manager');

  if (input.systemCRole !== undefined && !hasElevated) {
    qualities.push('employee');
  }

  return sortQualities(qualities);
}

/** Cle i18n pour une qualite cumulee (`gest:personRole.*`). */
export function personQualityI18nKey(role: DerivedPersonRole): `gest:personRole.${DerivedPersonRole}` {
  return personRoleI18nKey(role);
}
